import { type Dispatch } from "@reduxjs/toolkit";
import React, { useEffect } from "react";
import { type ConnectedProps, connect } from "react-redux";
import ReportingExport from "../../../../components/reportingExport/ReportingExport";
import ReportingFilter from "../../../../components/reportingFilter/ReportingFilter";

import AssessmentPerformanceBody from "./AssessmentPerformanceBody";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { type RootState } from "../../../Application/globaltypes/redux";
import { usePerformanceBreadcrumbs } from "../../Common/Hooks/usePerformanceBreadcrumbs";
import {
  reset,
  selectEngagementQuestionsStatus,
  selectEngagementQuestionsValue,
} from "../state/slices/assessmentPerformanceSlice";

import { FeatureFlags } from "featureFlags";
import { selectIsExportActive } from "features/Reporting/state/export/exportSlice";
import { type IWizardStep, bindAction } from "interfaces";

import "../../Common/utils/performanceSCSSUtils.scss";
import "./AssessmentPerformance.scss";
import { useAccountFilterShouldShow } from "features/Reporting/Content/queries/useAccountFilterShouldShow";
import { usePerformanceFilter } from "features/Library/Common/Hooks/usePerformanceFilter";
import { assessmentsStateSelector } from "../state/selectors";

export type Props = PropsFromRedux &
  IWizardStep & {
    entityId: number;
    entityTitle: string;
  };

export const AssessmentPerformance: React.FC<Props> = ({
  entityId,
  entityTitle,
  resetState,
  acceptHandlers,
  isExportEnabled,
  accountId,
  accountType,
  publisherId,
}) => {
  const accountFilterShouldShow = useAccountFilterShouldShow(accountId, accountType, true);

  const [dateFilter, setDateFilter] = usePerformanceFilter(
    {
      includeAccountsDropdown: accountFilterShouldShow,
      accountId,
      supportsSPType: true,
      includeDistinct: true,
    },
    publisherId,
  );

  const reportEnabled = useFeatureFlag(FeatureFlags.AssessmentReport);
  const { domElements, registerBreadcrumb, hasBreadcrumbsToDisplay } = usePerformanceBreadcrumbs();

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  useEffect(() => {
    acceptHandlers?.({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  return (
    <div className="assessmentPerformanceRoot">
      <header className="assessmentHeader">
        {hasBreadcrumbsToDisplay ? (
          <div className="breadCrumbs">{domElements}</div>
        ) : (
          <h2 className="assessmentTitle">Summary</h2>
        )}
        <div className="assessmentActions">
          <ReportingFilter
            currentFilter={dateFilter}
            callback={setDateFilter}
            includeAccountsDropdown={accountFilterShouldShow}
            includeAccounts={false}
            supportsSPType
            publisherId={publisherId}
            includeDistinct
          />
          {isExportEnabled && <ReportingExport />}
        </div>
      </header>
      <div className="performanceRoot">
        <AssessmentPerformanceBody
          includeAccounts={accountFilterShouldShow && (dateFilter.accounts?.length ?? 0) > 0}
          entityId={entityId}
          entityTitle={entityTitle}
          registerBreadcrumb={registerBreadcrumb}
          dateFilter={dateFilter}
        />
      </div>
    </div>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    engagementQuestionsStatus: selectEngagementQuestionsStatus(state),
    engagementQuestionsData: selectEngagementQuestionsValue(state),
    isExportEnabled: selectIsExportActive(state),
    accountId: state.userProfile.accountId,
    accountType: state.userProfile.accountTypeId,
    publisherId: assessmentsStateSelector(state).base.assessmentDetailsReducer.assessment.ownerId,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: Dispatch) => ({
  resetState: bindAction(reset, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(AssessmentPerformance);
export default ConnectedComponent;
